/**
 * «Поставщики» — §6 дополнения о сверке: по каждому поставщику с отсрочкой
 * долг в системе, просрочка, долг по банку и последний акт. Сверху —
 * метка свежести выписки (§16.3 ТЗ платформы) и кнопка загрузки.
 * Поставщики «расчёт при получении» — внизу, свёрнуто: долга у них не
 * бывает (D78).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Icon } from '../../icons';
import { Link, navigate } from '../../router';
import { isoDate } from '../../ui';
import { LIVE, issuesText, plural, reconcileApi } from './api';
import type { ActStatus, Overview, OverviewSupplier } from './api';
import { TAB_PATHS, ledgerPath } from './paths';

export const overviewKey = ['reconcile', 'overview'] as const;

export function useOverview() {
  return useQuery<Overview, ApiError>({
    queryKey: overviewKey,
    queryFn: () => reconcileApi.overview(),
    ...LIVE,
  });
}

/** Значок акта: ✓ сошёлся, ⚠ N расхождений, «прочитан не полностью». */
export function ActBadge({
  status,
  issues,
  stale = false,
}: {
  status: ActStatus;
  issues: number;
  stale?: boolean;
}) {
  const text =
    status === 'ok'
      ? '✓ сошёлся'
      : status === 'issues'
        ? `⚠ ${issuesText(issues)}`
        : 'прочитан не полностью';
  return (
    <span
      className={`rc-badge rc-badge--${status}${stale ? ' rc-badge--stale' : ''}`}
      title={stale ? 'Акт старше 60 дней — попросите у поставщика свежий' : undefined}
    >
      {text}
    </span>
  );
}

function LastAct({ supplier }: { supplier: OverviewSupplier }) {
  const act = supplier.last_act;
  if (!act) return <span className="faint">нет</span>;
  return (
    <span className={`rc-last-act${act.stale ? ' rc-last-act--stale' : ''}`}>
      <span>{act.act_date ? isoDate(act.act_date) : 'без даты'}</span>
      <ActBadge status={act.status} issues={act.issues} stale={act.stale} />
    </span>
  );
}

export function Freshness({ data }: { data: Overview }) {
  return (
    <div className="rc-fresh">
      <p className={data.fresh.statement_to ? 'rc-fresh__text' : 'rc-fresh__text rc-fresh__text--none'}>
        {data.fresh.statement_to
          ? `Выписка загружена по ${isoDate(data.fresh.statement_to)}`
          : 'Выписка ещё не загружена — загрузите её во вкладке «Выписка банка»'}
      </p>
      {data.can_edit && (
        <button
          type="button"
          className="button button--primary button--small"
          onClick={() => navigate(TAB_PATHS.statements)}
        >
          Загрузить выписку банка
        </button>
      )}
    </div>
  );
}

export function Suppliers() {
  const overview = useOverview();
  const [search, setSearch] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);

  const rows = useMemo(() => {
    const list = overview.data?.suppliers ?? [];
    const needle = search.trim().toLocaleLowerCase('ru');
    return list
      .filter((s) => !needle || s.name.toLocaleLowerCase('ru').includes(needle))
      .filter((s) => !onlyIssues || s.issues > 0)
      .sort((a, b) => Number(b.system_debt) - Number(a.system_debt));
  }, [overview.data, search, onlyIssues]);

  if (overview.isPending) return <Loading />;
  if (overview.error) {
    return <ErrorState error={overview.error} onRetry={() => overview.refetch()} />;
  }
  const data = overview.data;
  const withIssues = data.suppliers.filter((s) => s.issues > 0).length;

  return (
    <div className="rc-stack">
      <Freshness data={data} />

      <p className="rc-todo">
        {!data.fresh.statement_to
          ? 'Начните с выписки банка: система разложит платежи по накладным и покажет, что уже оплачено.'
          : withIssues > 0
            ? `У ${withIssues} ${plural(withIssues, ['поставщика', 'поставщиков', 'поставщиков'])} есть расхождения — откройте поставщика, чтобы увидеть, что не сходится.`
            : 'Расхождений нет. Загружайте свежую выписку после каждого дня оплат, а акты сверки — когда их пришлёт поставщик.'}
      </p>

      <div className="rc-filters">
        <label className="search rc-search">
          <Icon name="search" size={16} />
          <input
            className="search__input"
            type="search"
            placeholder="Поставщик — часть названия"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Поиск поставщика"
          />
        </label>
        <label className="rc-check">
          <input
            type="checkbox"
            checked={onlyIssues}
            onChange={(event) => setOnlyIssues(event.target.checked)}
          />
          есть расхождения
        </label>
      </div>

      <section className="rc-card" aria-label="Поставщики">
        {rows.length === 0 ? (
          <Empty
            title={data.suppliers.length === 0 ? 'Поставщиков с отсрочкой нет' : 'Никого не нашлось'}
            text={
              data.suppliers.length === 0
                ? 'Список берётся из накладных StoreHouse. Как только придут накладные поставщиков, они появятся здесь.'
                : onlyIssues
                  ? 'Расхождений нет ни у кого из подходящих. Снимите галочку «есть расхождения», чтобы увидеть всех.'
                  : 'Проверьте написание или сотрите поиск.'
            }
          />
        ) : (
          <div className="rc-table rc-suppliers" role="table" aria-label="Долги поставщикам">
            <div className="rc-suppliers__row rc-table__head" role="row">
              <span role="columnheader">Поставщик</span>
              <span role="columnheader" className="num">Долг в системе</span>
              <span role="columnheader" className="num">Просрочено</span>
              <span role="columnheader" className="num">Долг по банку</span>
              <span role="columnheader">Последний акт</span>
              <span role="columnheader" className="num">Расхождения</span>
            </div>
            {rows.map((supplier) => (
              <Link
                key={supplier.id}
                to={ledgerPath(supplier.id)}
                className={`rc-suppliers__row rc-supplier${supplier.issues > 0 ? ' rc-supplier--issues' : ''}`}
                role="row"
              >
                <span role="cell" className="rc-supplier__name">{supplier.name}</span>
                <span role="cell" className="num" data-label="Долг в системе">
                  {rub(supplier.system_debt)}
                </span>
                <span
                  role="cell"
                  className={`num${Number(supplier.overdue) > 0 ? ' rc-overdue' : ' faint'}`}
                  data-label="Просрочено"
                >
                  {Number(supplier.overdue) > 0 ? rub(supplier.overdue) : '—'}
                </span>
                <span role="cell" className="num" data-label="Долг по банку">
                  {supplier.bank_debt === null ? <span className="faint">—</span> : rub(supplier.bank_debt)}
                </span>
                <span role="cell" data-label="Последний акт">
                  <LastAct supplier={supplier} />
                </span>
                <span role="cell" className="num" data-label="Расхождения">
                  {supplier.issues > 0 ? (
                    <span className="rc-issue-count">{supplier.issues}</span>
                  ) : (
                    <span className="faint">нет</span>
                  )}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {data.immediate.length > 0 && (
        <details className="rc-card rc-immediate">
          <summary>
            Расчёт при получении — {data.immediate.length}{' '}
            {plural(data.immediate.length, ['поставщик', 'поставщика', 'поставщиков'])}
            <span className="faint"> · долга у них не бывает, лента открывается так же</span>
          </summary>
          <ul className="rc-plain-list">
            {data.immediate.map((supplier) => (
              <li key={supplier.id}>
                <Link to={ledgerPath(supplier.id)}>{supplier.name}</Link>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
